import { isRecoverableRefreshTokenError } from '@risksmart-app/components/src/utils/errorUtils';
import {
  isTrpcAuthNotReadyError,
  isTrpcFetchAbortError,
  isTrpcUnauthorizedError,
} from '@risksmart-app/components/src/utils/trpc/classification';
import {
  findErrorInCauseChain,
  TrpcTokenRefreshError,
} from '@risksmart-app/components/src/utils/trpc/errors';
import type {
  Query,
  QueryMeta,
  QueryObserverResult,
  RefetchOptions,
} from '@tanstack/react-query';
import type { TRPCClientErrorLike } from '@trpc/client';
import { isTRPCClientError } from '@trpc/client';

import { isPageUnloading } from './pageUnload';

/**
 * Web's own tRPC helpers: how a failure is *reported*, and how a tRPC read is
 * bridged onto the GraphQL shapes it replaces.
 *
 * The transport policy — error classification, the retry bounds, the fetch
 * wrapper that keeps a status alive, the auth-abort error types — is shared
 * with the third-party portal and lives in
 * `@risksmart-app/components/src/utils/trpc/` (COI-159). It moved because both
 * apps are the same browser → tRPC hop, and the portal's copy of the abort
 * helpers had already drifted into being dead weight next to a react-query
 * default that retried every 403 three times.
 */

/**
 * Query freshness windows (COI-882). These live here rather than in `trpc.ts`
 * so a hook can import one without pulling the QueryClient module in with it —
 * that import is what breaks module mocks in the web test setup.
 *
 * Prod measurement behind the numbers: 2,171 tRPC requests in one hour resolved
 * to 232 distinct queries. 89% of production traffic was an exact repeat of a
 * query already made, and half of ALL traffic was 16 unchanging queries
 * re-fetched on every navigation.
 */

/**
 * Default freshness for every query — a dedupe window, not a cache policy.
 * See the `staleTime` note in `trpc.ts` for why it is seconds and not
 * milliseconds.
 */
export const DEDUPE_WINDOW_MS = 10_000;

/**
 * Register/badge counts (`useGetIssueCount` and its 18 siblings, rendered
 * through `ConnectedCount`).
 *
 * Measured at ~70 calls per hour EACH with a single distinct input — the same
 * count, recomputed on every navigation. A count is an aggregate over a
 * register: it changes when someone creates or deletes a record, and the
 * mutation hooks already invalidate the count query families for the actor who
 * did it (see the cache-invalidation-parity rule in the package CLAUDE.md). So
 * this window only governs how quickly a badge notices ANOTHER user's write,
 * where a minute is not a meaningful delay on a governance register.
 */
export const COUNT_STALE_TIME_MS = 60_000;

/**
 * Org-level reference data — departments, the user directory, entity/register
 * definitions, form customisation. Administrative config that changes on the
 * order of days, re-fetched on every navigation today (`department.allTypes`
 * and `user.directory` each ~77 calls/hour against ONE distinct input).
 *
 * Writes to these go through mutation hooks that invalidate their own query
 * family, so an admin editing a department still sees it immediately; this
 * window only delays how fast OTHER sessions pick the change up.
 */
export const REFERENCE_DATA_STALE_TIME_MS = 5 * 60_000;

/**
 * Spread into a query's options to opt it out of the global Sentry reporting in
 * `QueryCache.onError` (src/utils/trpc.ts):
 *
 * ```ts
 * trpc.frontend.g2Review.promptState.queryOptions(undefined, {
 *   ...bestEffortQueryMeta,
 *   retry: false,
 * })
 * ```
 *
 * Only for genuinely fire-and-forget reads — a query whose failure is already
 * handled by rendering nothing, where the user is neither blocked nor told, so
 * a Sentry event is pure noise (COI-716). Every other query keeps reporting;
 * do NOT reach for this to quieten a failure someone should be looking at.
 */
export const bestEffortQueryMeta = {
  meta: { reportErrors: false },
} satisfies { meta: QueryMeta };

/**
 * Whether the failed query opted out of error reporting via
 * `bestEffortQueryMeta`. `QueryMeta` values are `unknown`, so this checks for
 * the literal `false` — an absent or malformed flag keeps reporting on.
 */
export const isErrorReportingOptedOut = (query: Pick<Query, 'meta'>): boolean =>
  query.meta?.reportErrors === false;

/**
 * The reporting decision behind the global `QueryCache.onError`
 * (src/utils/trpc.ts) — kept here so it is unit-testable without stubbing the
 * Sentry transport.
 *
 * Reports by default. A **fetch-level abort** is suppressed only when the
 * teardown is attributable to us rather than to the user's connection:
 *
 *  - **nobody wants the data any more** — `isActive()` is false when the query
 *    has no observers (unmounted, #7723), when every observer has `enabled:
 *    false`, or when it was removed from the cache. All three are our own
 *    cancellation, so the resulting abort is noise.
 *  - **the document is going away** — a cross-document navigation kills every
 *    in-flight request while components stay mounted and enabled, so
 *    `isActive()` can't see it (see `isPageUnloading`).
 *
 * Anything else reports, including a fetch abort on a live, enabled query with
 * no unload in progress — that is the genuine-connectivity signal this handler
 * exists for. Non-auth 4xx and 5xx failures always report.
 *
 * A query carrying `bestEffortQueryMeta` is exempt from all of the above: it
 * opts out of reporting entirely, not just for aborts.
 *
 * Widened from a bare observer count in COI-716 — the count alone missed every
 * still-mounted case, which was ~all of RISKSMART-APP-B5P.
 */
export const shouldReportTrpcQueryError = (
  error: unknown,
  query: Pick<Query, 'isActive' | 'meta'>
): boolean => {
  if (isErrorReportingOptedOut(query)) {
    return false;
  }

  // The auth redirect link owns a 401: it clears the session and sends the
  // browser through /logout. An expired/missing session is expected lifecycle,
  // so reporting the same handled response from QueryCache only creates a
  // duplicate Sentry event for every query that happened to be in flight.
  if (isTrpcUnauthorizedError(error)) {
    return false;
  }

  // Auth-not-ready aborts (COI-897) are a start-up race that React Query's
  // retries resolve on their own. Reporting them would just move the 159
  // server-side UnauthorizedErrors this fix removes into Sentry instead.
  if (isTrpcAuthNotReadyError(error)) {
    return false;
  }

  // A failed silent refresh reports only when it is not one of the expected
  // session-lifecycle rejections — same split the GraphQL path applies in
  // ApolloGraphqlProvider.getAuthHeaders (RSP-4478). Either way the user is
  // already being redirected to /logout, so the event is for us, not them.
  const tokenRefreshError = findErrorInCauseChain(error, TrpcTokenRefreshError);
  if (tokenRefreshError) {
    return !isRecoverableRefreshTokenError(tokenRefreshError.cause);
  }

  if (!isTrpcFetchAbortError(error)) {
    return true;
  }

  return query.isActive() && !isPageUnloading();
};

/**
 * tRPC twin of `isPermissionError` (src/utils/graphqlUtils.ts). The Apollo
 * path surfaces a denied action as a GraphQL error with
 * `extensions.code === 'permission-error'`; the tRPC path surfaces the same
 * denial as a `TRPCClientError` whose formatted `code` is `'FORBIDDEN'`
 * (the data-layer returns 403 for a non-author edit; the tRPC service maps
 * 403 → FORBIDDEN).
 *
 * The formatted code lives on `error.data.code` — the same accessor
 * `mapFormError` (src/utils/formError.ts) reads. `error.shape`
 * carries the raw formatter output; check `shape.data.code` too so the helper
 * still fires if `data` is momentarily unpopulated.
 *
 * Used by the comments editor's `tinycomments_edit_comment` handler
 * (RSP-6496) to treat a non-author edit as `canEdit: false` under EITHER data
 * path behind the `trpc` flag.
 */
export const isTrpcPermissionError = (e: unknown): boolean => {
  if (!isTRPCClientError(e)) {
    return false;
  }

  return e.data?.code === 'FORBIDDEN' || e.shape?.data?.code === 'FORBIDDEN';
};

export const mapTRPCRefetch = async <TTRPCData, TGraphQLQuery>(
  trpcRefetch: (options?: RefetchOptions) => Promise<
    QueryObserverResult<
      TTRPCData,
      TRPCClientErrorLike<{
        transformer: true;
        errorShape: unknown;
      }>
    >
  >,
  trpcToGraphQLMapper: (data: TTRPCData) => TGraphQLQuery
) => {
  const { data: refetchData, error } = await trpcRefetch();

  const mappedRefetchData = refetchData
    ? trpcToGraphQLMapper(refetchData)
    : undefined;

  return { data: mappedRefetchData, error: error ?? null };
};
